import React, { useEffect, useContext, useState } from 'react';

import {
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
} from '@material-ui/core';
import { ConfigCard } from '../../../../shared/components/ConfigCard';
import { Spacer, NormalText, BoldText } from '../../styles';
import { CustomTextField } from '../../../DevolutionPageConfig/components/CustomTextField';
import { CustomRow, Column } from './styles';
import Services from '../../services';
import { GeneralContext } from '../../../../core/app-context/GeneralContext';
import { PrivatePageStructure } from '../../../../shared/components/PrivatePageStructure';
import usePageStore from '../../store/pageStore';
import TitlePage from '../../../../shared/components/TitlePage';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { EmailPreview } from '../EmailPreview';

const _getInfoFromType = (type) => {
  switch (type) {
    case 'select-return-method':
      return {
        title: 'Email de seleção do método de devolução',
        tags: (
          <>
            <NormalText>{'{{clientName}}'}</NormalText>
            <NormalText>{'{{orderNumber}}'}</NormalText>
            <NormalText>{'{{selectReturnLocationUrl}}'}</NormalText>
            <NormalText>{'{{items}} -> Array<{item, price}>'}</NormalText>
          </>
        ),
      };
    case 'cliqueretire-approved':
      return {
        title: 'Email de aprovação pedidos CliqueRetire',
        tags: (
          <>
            <NormalText>{'{{clientName}}'}</NormalText>
            <NormalText>{'{{orderNumber}}'}</NormalText>
            <NormalText>{'{{externalOrderNumber}}'}</NormalText>
            <NormalText>{'{{externalOrderId}}'}</NormalText>
            <NormalText>{'{{expressNumber}}'}</NormalText>
            <NormalText>{'{{qrCodeUrl}}'}</NormalText>
            <NormalText>{'{{lockerAddr}}'}</NormalText>
            <NormalText>{'{{eboxCode}}'}</NormalText>
            <NormalText>{'{{eboxName}}'}</NormalText>
            <NormalText>{'{{eboxRouteLink}}'}</NormalText>
            <NormalText>{'{{expirationDate}}'}</NormalText>
          </>
        ),
      };
    case 'correios-approved':
      return {
        title: 'Email de aprovação pedidos Correios',
        tags: (
          <>
            <NormalText>{'{{clientName}}'}</NormalText>
            <NormalText>{'{{orderNumber}}'}</NormalText>
            <NormalText>{'{{eTicket}}'}</NormalText>
          </>
        ),
      };
    default:
      return {
        title: 'Page title',
      };
  }
};

export default function EmailConfigurations({ type }) {
  const { store, setErrorMessage, setSuccessMessage } =
    useContext(GeneralContext);
  const { title, tags } = _getInfoFromType(type);

  const fetchedData = usePageStore((state) => state.fetchedData);
  const setFetchedData = usePageStore((state) => state.setFetchedData);
  const emailData = usePageStore((state) => state.emailData);
  const setEmailData = usePageStore((state) => state.setEmailData);
  const setMode = usePageStore((state) => state.setMode);
  const setSubject = usePageStore((state) => state.setSubject);
  const setHtml = usePageStore((state) => state.setHtml);
  const setLoadingSaveChanges = usePageStore(
    (state) => state.setLoadingSaveChanges,
  );
  const setLoadingTemplateEmail = usePageStore(
    (state) => state.setLoadingTemplateEmail,
  );
  const loadingTemplateEmail = usePageStore(
    (state) => state.loadingTemplateEmail,
  );
  const loadingSaveChanges = usePageStore((state) => state.loadingSaveChanges);

  const [showPreview, setShowPreview] = useState(false);

  const getTemplateEmail = async () => {
    setLoadingTemplateEmail(true);
    const response = await Services.getTemplateEmail({
      storeId: store?.id,
      type,
    });
    if (!response) {
      setLoadingTemplateEmail(false);
      return;
    }
    delete response.__typename;
    delete response.storeId;
    setFetchedData(response, type);
    setEmailData(response, type);
    setLoadingTemplateEmail(false);
  };

  useEffect(() => {
    if (!fetchedData[type]) {
      getTemplateEmail();
    }
  }, []);

  const handleSaveChanges = async () => {
    setLoadingSaveChanges(true);
    const response = await Services.upsertTemplateEmail({
      storeId: store.id,
      templateEmail: { ...emailData[type], type },
    });
    if (!response) {
      setErrorMessage('Erro ao salvar template do email');
      setLoadingSaveChanges(false);
      return;
    }
    setLoadingSaveChanges(false);
    setSuccessMessage('Alterações salvas com sucesso');
    setFetchedData(response, type);
    setEmailData(response, type);
  };

  const handleDiscartChanges = () => {
    if (fetchedData[type]) {
      setEmailData(fetchedData[type], type);
    } else {
      setEmailData(
        {
          mode: 'default',
          subject: '',
          html: '',
        },
        type,
      );
    }
    setSuccessMessage('Alterações descartadas com sucesso');
  };

  const isCustom = emailData[type].mode === 'custom';
  return (
    <>
      {showPreview ? (
        <EmailPreview
          closeCb={() => setShowPreview(false)}
          subject={emailData[type].subject}
          html={emailData[type].html}
        />
      ) : (
        <PrivatePageStructure
          title={
            isCustom ? (
              <OpenEmailButton
                title={title}
                onClick={() => setShowPreview(true)}
              />
            ) : (
              title
            )
          }
          loading={loadingTemplateEmail}
          btn1Action={handleDiscartChanges}
          btn2Action={handleSaveChanges}
          btn2Loading={loadingSaveChanges}
          enableBackButton
          backButtonDestPage="/emails"
        >
          <ConfigCard
            title="Template do email"
            subtitle="Escolha se o email será enviado com o template padrão ou com um HTML personalizado."
            flexDirection="column"
            alignItems="flex-start"
          >
            <FormControl>
              <RadioGroup
                name="email-type-selection"
                value={emailData[type].mode}
                onChange={(e) => setMode(e.target.value, type)}
                defaultValue="default"
              >
                <FormControlLabel
                  value="default"
                  control={<Radio size="small" color="primary" />}
                  label={<NormalText fontSize="14px">Tema padrão</NormalText>}
                />
                <FormControlLabel
                  value="custom"
                  control={<Radio size="small" color="primary" />}
                  label={
                    <NormalText fontSize="14px">HTML Personalizado</NormalText>
                  }
                />
              </RadioGroup>
            </FormControl>
          </ConfigCard>
          <Spacer vertical />
          <ConfigCard
            title="Conteúdo do email"
            subtitle="Configure o conteúdo do email."
            flexDirection="column"
            alignItems="flex-start"
          >
            <NormalText>Assunto</NormalText>
            <Spacer vertical height="5px" />
            <CustomTextField
              width="500px"
              value={emailData[type].subject}
              setValue={(val) => setSubject(val, type)}
            />
            {isCustom && (
              <CustomRow>
                <Column>
                  <NormalText>Corpo do email (HTML)</NormalText>
                  <Spacer vertical height="5px" />
                  <CustomTextField
                    multiline
                    rows={10}
                    maxRows={15}
                    width="500px"
                    value={emailData[type].html}
                    setValue={(val) => setHtml(val, type)}
                  />
                </Column>
                <Spacer horizontal width="10px" />
                <Column>
                  <BoldText style={{ margin: '0px' }}>
                    Tags para utilização:
                  </BoldText>
                  <Spacer vertical height="5px" />
                  {tags}
                </Column>
              </CustomRow>
            )}
          </ConfigCard>
        </PrivatePageStructure>
      )}
    </>
  );
}

function OpenEmailButton({ onClick, title }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
      }}
    >
      <TitlePage label={title} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          cursor: 'pointer',
        }}
        onClick={onClick}
      >
        <VisibilityOutlinedIcon
          style={{ paddingRight: '6px', fontSize: '18' }}
        />
        <Typography style={{ fontSize: 12 }}>Visualizar</Typography>
      </div>
    </div>
  );
}
